/**
 * Diff sheet. Screen spec S11, the pending changes rail.
 *
 * THE ARGUMENT FOR THIS COMPONENT, in one sentence: a first time builder is
 * about to write to the database their live app is reading, and the only
 * honest way to let them do that is to show them exactly what is about to
 * change, in their own words, before anything is written.
 *
 * So this is not a confirmation dialog. It carries no warning tone, no
 * attention color and no dangerous verb, because editing a row is a normal
 * thing to do and treating it as an incident would teach a person to click
 * through the sheet that matters. What it carries is a ledger: one line per
 * cell, the old value beside the new one, both in mono, grouped by the row a
 * person recognizes rather than by the identifier the database uses.
 *
 * The old value is the fact people check, so it is never dropped and never
 * abbreviated. Two identical looking values on one line is a real outcome
 * here: it means somebody typed a space, and the sheet is where they find
 * that out rather than after the write.
 *
 * Rows added and not yet saved are listed under their own heading. An insert
 * has no before, and pretending it does by printing an empty column would be
 * inventing a row that never existed.
 */

import { useEffect } from 'react';
import { Button } from '../primitives/Button';
import { Dialog } from '../primitives/Dialog';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import type { DbCell } from '../../lib/types';
import '../../styles/components/diff-sheet.css';

export interface CellChange {
  rowId: string;
  /** The row's most human column, so a person recognizes what they edited. */
  rowLabel: string;
  column: string;
  before: DbCell;
  after: DbCell;
}

export interface RowAddition {
  rowId: string;
  rowLabel: string;
}

export interface DiffSheetProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  /** The table being written to, named in the sheet's one grounding line. */
  table: string;
  changes: CellChange[];
  additions?: RowAddition[];
  /** True while the write is in flight. The action holds its own width. */
  saving?: boolean;
  /** Rendered inside the page rather than a portal, for the kit gallery. */
  inline?: boolean;
}

function literal(value: DbCell): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (value === '') return t('empty');
  return String(value);
}

export function DiffSheet({
  open,
  onClose,
  onSave,
  table,
  changes,
  additions = [],
  saving = false,
  inline = false,
}: DiffSheetProps) {
  const total = changes.length + additions.length;

  useEffect(() => {
    if (open) {
      captureEvent('diff_sheet_opened', {
        table,
        changes_count: changes.length,
        additions_count: additions.length,
      });
    }
  }, [open, table, changes.length, additions.length]);

  // Grouped in the order the rows were edited, not sorted, because the order
  // a person made their changes in is the order they remember them in.
  const groups: { rowId: string; rowLabel: string; cells: CellChange[] }[] = [];
  for (const change of changes) {
    const found = groups.find((group) => group.rowId === change.rowId);
    if (found) found.cells.push(change);
    else groups.push({ rowId: change.rowId, rowLabel: change.rowLabel, cells: [change] });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      inline={inline}
      title={t('Review your changes')}
      description={t('Nothing in {table} changes until you save.', { table })}
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              captureEvent('diff_sheet_keep_editing_clicked', { table });
              onClose();
            }}
            data-autofocus=""
          >
            {t('Keep editing')}
          </Button>
          <Button
            variant="solid"
            loading={saving}
            onClick={() => {
              captureEvent('diff_sheet_save_clicked', {
                table,
                total_changes: total,
              });
              onSave();
            }}
          >
            {total === 1 ? t('Save 1 change') : t('Save {n} changes', { n: total })}
          </Button>
        </>
      }
    >
      <div className="diff">
        {groups.length > 0 ? (
          <div className="diff__head" aria-hidden="true">
            <span className="diff__label">{t('COLUMN')}</span>
            <span className="diff__label">{t('WAS')}</span>
            <span className="diff__label">{t('NOW')}</span>
          </div>
        ) : null}

        {groups.map((group) => (
          <section className="diff__group" key={group.rowId}>
            <h3 className="diff__row-name mono">{group.rowLabel}</h3>
            <ul className="diff__cells">
              {group.cells.map((change) => (
                <li className="diff__cell" key={`${change.rowId}-${change.column}`}>
                  <span className="diff__col mono">{change.column}</span>
                  <span className="diff__was mono">{literal(change.before)}</span>
                  <span className="diff__now mono">{literal(change.after)}</span>
                  {/* The visible line is three columns of mono. This is the
                      same line as one sentence, for anyone reading it out. */}
                  <span className="diff__say">
                    {t('{column} changes from {before} to {after}.', {
                      column: change.column,
                      before: literal(change.before),
                      after: literal(change.after),
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {additions.length > 0 ? (
          <section className="diff__group">
            <h3 className="diff__added">
              {additions.length === 1
                ? t('1 new row')
                : t('{n} new rows', { n: additions.length })}
            </h3>
            <ul className="diff__cells">
              {additions.map((addition) => (
                <li className="diff__new mono" key={addition.rowId}>
                  {addition.rowLabel}
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}
